/**
 * SLD Viewer — shared constants, helpers, and pure functions.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import type {
  DataPointConfig,
  DataPointRuntimeState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
} from '@/types/monitoring';
import type { SLDPointMapping, SLDAdvancedRuleMapping } from '@/stores/monitoringStore';

// ── Stable empty-array constants (avoid unstable ?? [] inline) ──
export const EMPTY_DATAPOINTS: DataPointConfig[] = [];
export const EMPTY_ADVANCED_RULES: AdvancedAlarmRule[] = [];
export const EMPTY_MAPPINGS: SLDPointMapping[] = [];
export const EMPTY_RULE_MAPPINGS: SLDAdvancedRuleMapping[] = [];
export const EMPTY_TRIGGERED: TriggeredAdvancedRule[] = [];

export const DRAG_THRESHOLD = 5; // px before mousedown+move counts as drag (not click)

// State -> CSS class mappings (use design-system variables via CSS classes)
export function stateClass(state: string): string {
  return `sld-state--${state}`;
}
export function stateTextClass(state: string): string {
  return `sld-state-text--${state}`;
}

export function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

export function formatMetricValue(v: number | string | null, decimals = 2): string {
  if (v == null) return '\u2014';
  if (typeof v === 'string') return v;
  return v.toFixed(decimals);
}

export const SEVERITY_COLORS: Record<string, string> = {
  ok: '#27AE60',
  warning: '#F2994A',
  critical: '#EB5757',
};

/**
 * Render metric values for a data point on the SLD.
 * mappingDisplayMetric controls what's shown:
 *   "" or undefined  -> nothing (label only)
 *   "__all__"        -> all selected_metrics
 *   specific key     -> only that metric
 * Always uses card-style layout (.sld-point-values) for consistent appearance.
 */
export function renderMetricValues(
  config: DataPointConfig | undefined,
  runtime: DataPointRuntimeState | undefined,
  mappingDisplayMetric?: string,
): JSX.Element | null {
  const vals = runtime?.values;
  if (!vals || !mappingDisplayMetric) return null;

  // Build the list of metrics to display
  let metricsToShow: string[];
  if (mappingDisplayMetric === '__all__') {
    metricsToShow = (config?.selected_metrics ?? []).filter(k => vals[k] != null);
  } else {
    metricsToShow = vals[mappingDisplayMetric] != null ? [mappingDisplayMetric] : [];
  }
  if (metricsToShow.length === 0) return null;

  // Build triggered metric -> color map
  const triggeredColors = new Map<string, string>();
  for (const tr of runtime?.triggered_rules ?? []) {
    triggeredColors.set(tr.metric, SEVERITY_COLORS[tr.severity] ?? SEVERITY_COLORS.ok);
  }

  // Always use card-style wrapper for consistent appearance
  return (
    <span className="sld-point-values">
      {metricsToShow.map(k => {
        const valColor = triggeredColors.get(k) ?? SEVERITY_COLORS.ok;
        return (
          <span key={k} className="sld-point-value-row">
            {metricsToShow.length > 1 && <span className="sld-point-value-key">{k}</span>}
            <span className="sld-point-value" style={{ color: valColor }}>
              {formatMetricValue(vals[k]!, config?.decimals)}
              {(() => { const u = config?.metric_units?.[k] ?? config?.unit; return u ? ` ${u}` : ''; })()}
            </span>
          </span>
        );
      })}
    </span>
  );
}

export function opLabel(op: string): string {
  switch (op) {
    case 'lt': return '<';
    case 'lte': return '\u2264';
    case 'gt': return '>';
    case 'gte': return '\u2265';
    case 'eq': return '=';
    case 'neq': return '\u2260';
    default: return op;
  }
}

export type PlacementType = 'datapoint' | 'rule';
export type SidePanelTab = 'datapoints' | 'rules';

// ── Multi-view shared constants & types ──
export const EMPTY_POINT_MAPPINGS: SLDPointMapping[] = [];
export const EMPTY_ADVANCED_RULE_MAPPINGS: SLDAdvancedRuleMapping[] = [];

export const MIN_WIN_W = 250;
export const MIN_WIN_H = 200;

export interface WindowLayout {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex: number;
}
